import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
  Highlighter,
  Loader2,
  MoreHorizontal,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { FileDocumentInfo, type DocumentInfoSeed } from '@/components/documents/FileDocumentInfo';
import { FilePreviewBrowser } from '@/components/documents/FilePreviewBrowser';
import { FileHighlightList } from '@/components/documents/FileHighlightList';
import { FileHighlightRail, type PdfPageMetric } from '@/components/documents/FileHighlightRail';
import { FileHighlightToolbar } from '@/components/documents/FileHighlightToolbar';
import { FilePreviewConfirm } from '@/components/documents/FilePreviewConfirm';
import { FileSideResizeHandle } from '@/components/documents/FileSideResizeHandle';
import { FilePreviewDocToolbar } from '@/components/documents/FilePreviewDocToolbar';
import { CollapsedToolRail } from '@/components/journal/ToolsColumn';
import {
  FilePreviewStage,
  type FilePreviewStageState,
} from '@/components/documents/FilePreviewStage';
import { Button } from '@/components/ui/Button';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import {
  useCreateDocumentHighlight,
  useDeleteDocumentHighlight,
  useDocumentHighlights,
  usePreviewDocument,
  useUpdateDocumentHighlight,
} from '@/hooks/useDocumentHighlights';
import {
  useDocumentHighlightTags,
  useSetDocumentHighlightTags,
} from '@/hooks/useDocumentHighlightTags';
import {
  activeHighlightTags,
  highlightColorForSelectedTags,
  highlightColorForTagToken,
  sameHighlightTagIds,
} from '@/lib/highlightTagColors';
import { useGetDocumentDownloadUrl, type DocumentRow } from '@/hooks/useDocuments';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { applyAnchoredScroll, useFilePreviewZoom } from '@/hooks/useFilePreviewZoom';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import { useAuth } from '@/lib/auth';
import type { DocumentInfoField } from '@/lib/documentInfo';
import {
  documentAccessFailureKey,
  DocumentStorageAccessError,
  isDocumentStorageAccessError,
} from '@/lib/documentStorageAccess';
import {
  isWordDocumentMime,
  resolveFileMimeType,
  resolveStoredDocumentMimeType,
} from '@/utils/fileMime';
import { previewBlobFromFile, previewBlobFromResponse } from '@/utils/filePreview';
import { docxPreviewHtml } from '@/utils/docxPreview';
import { readPreviewBody, type PreviewBody } from '@/utils/previewContent';
import { classifyPreview, previewKindLabelKey } from '@/utils/previewKind';
import { syncPreviewChrome } from '@/utils/previewChrome';
import { withTimeout } from '@/utils/previewTimeout';
import {
  bindHighlightSelection,
  clampHighlightZoom,
  fitHighlightZoom,
  pageSideMarginPx,
  filterNumberedHighlights,
  highlightFilterCounts,
  numberHighlights,
  clampFileSideWidth,
  displayedFileSideWidths,
  FILE_SIDE_WIDTH_DEFAULT,
  FILE_SIDE_WIDTH_MIN,
  fileSideOccupied,
  maxFileSideWidth,
  readFileSideWidth,
  readFilesPanelOpen,
  readHighlightsVisible,
  readMarkingPanelOpen,
  sameHighlightZoom,
  writeFileSideWidth,
  writeFilesPanelOpen,
  writeHighlightsVisible,
  writeMarkingPanelOpen,
  type FileSidePresence,
  type FileHighlightDraft,
  type HighlightColor,
  type HighlightFilter,
  type HighlightSelectionAnalysis,
  type HighlightVisibility,
} from '@/utils/fileHighlights';
import { cn } from '@/utils/cn';
import { flowContentBoxWidth, flowFitZoom } from '@/utils/flowPreviewZoom';
import { marginRingOffsets, markingScrollHost } from '@/utils/marginRings';
import { syncHtmlHighlightMarks } from '@/utils/htmlHighlight';
import { syncSheetHighlightMarks } from '@/utils/sheetHighlight';
import { assignScrollTopToReveal } from '@/utils/previewScroll';
import { downloadBytesAsFile, downloadUrlAsFile } from '@/utils/documentDownload';
import { embedHighlightsInPdfCopy, markedDownloadFilename } from '@/utils/pdfHighlightExport';

const PdfCanvasPreview = lazy(() => import('@/components/documents/PdfCanvasPreview'));

export type FilePreviewSource =
  | {
      type: 'document';
      documentId: string;
      filename: string;
      mimeType: string;
      /** Open the preview with this file marking selected. */
      highlightId?: string;
    }
  | { type: 'local'; file: File };

interface FilePreviewOverlayProps {
  source: FilePreviewSource;
  onClose: () => void;
}

type RailTab = 'markings' | 'info';

function previewSourceKey(source: FilePreviewSource): string {
  if (source.type === 'local') {
    return `local:${source.file.name}:${source.file.size}:${source.file.lastModified}`;
  }
  return `doc:${source.documentId}:${source.highlightId ?? ''}`;
}

export function FilePreviewOverlay({ source, onClose }: FilePreviewOverlayProps) {
  const externalKey = previewSourceKey(source);
  const [trackedKey, setTrackedKey] = useState(externalKey);
  const [browsed, setBrowsed] = useState<FilePreviewSource | null>(null);
  if (trackedKey !== externalKey) {
    setTrackedKey(externalKey);
    setBrowsed(null);
  }
  const view = trackedKey === externalKey ? (browsed ?? source) : source;
  const browseIds = useRef({ clinicId: null as string | null, patientId: null as string | null });
  const railResetKey = useRef<string | null>(null);
  const panelRestoreKey = useRef<string | null>(null);

  const { t } = useTranslation();
  const { user, isClinicAdmin } = useAuth();
  const desktop = useIsDesktopNav();
  const { mutateAsync: fetchDocumentUrl } = useGetDocumentDownloadUrl();
  const documentId = view.type === 'document' ? view.documentId : null;
  const previewDoc = usePreviewDocument(documentId);
  if (previewDoc.data) {
    browseIds.current = {
      clinicId: previewDoc.data.clinic_id,
      patientId: previewDoc.data.patient_id,
    };
  }
  const browseClinicId = previewDoc.data?.clinic_id ?? browseIds.current.clinicId;
  const browsePatientId = previewDoc.data?.patient_id ?? browseIds.current.patientId;
  const highlightsQuery = useDocumentHighlights(documentId);
  const createHighlight = useCreateDocumentHighlight();
  const updateHighlight = useUpdateDocumentHighlight();
  const highlightTagsQuery = useDocumentHighlightTags(previewDoc.data?.clinic_id);
  const setHighlightTags = useSetDocumentHighlightTags();
  const highlightTags = useMemo(() => highlightTagsQuery.data ?? [], [highlightTagsQuery.data]);
  const deleteHighlight = useDeleteDocumentHighlight();
  const clinicId = previewDoc.data?.clinic_id ?? null;
  const { data: members } = useClinicMembers(clinicId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<HTMLElement>(null);
  const docxRef = useRef<HTMLDivElement>(null);
  const zoomSourceRef = useRef(source);
  const pendingFlowAnchor = useRef<{ origin: { x: number; y: number }; ratio: number } | null>(
    null,
  );
  const railInnerRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef(false);
  const infoSeedId = useRef(0);
  const openedHighlightRef = useRef<string | null>(null);
  const toolbarTagTouched = useRef(false);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [content, setContent] = useState<PreviewBody | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [actionUrl, setActionUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [inlineFailed, setInlineFailed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [draft, setDraft] = useState<FileHighlightDraft | null>(null);
  const [multiPageHint, setMultiPageHint] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [toolbarColor, setToolbarColor] = useState<HighlightColor>('yellow');
  const [toolbarTagIds, setToolbarTagIds] = useState<string[]>([]);
  const [toolbarVisibility, setToolbarVisibility] = useState<HighlightVisibility>('clinic');
  const [toolbarComment, setToolbarComment] = useState('');
  const [editColor, setEditColor] = useState<HighlightColor>('yellow');
  const [editTagIds, setEditTagIds] = useState<string[]>([]);
  const [editVisibility, setEditVisibility] = useState<HighlightVisibility>('clinic');
  const [editComment, setEditComment] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [scrollToPage, setScrollToPage] = useState<number | null>(null);
  const [panelOpen, setPanelOpen] = useState(() => readMarkingPanelOpen(user?.id));
  const [filesOpen, setFilesOpen] = useState(() => readFilesPanelOpen(user?.id));
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? FILE_SIDE_WIDTH_DEFAULT * 4 : window.innerWidth,
  );
  const [filesPref, setFilesPref] = useState(() => readFileSideWidth(user?.id, 'files'));
  const [markingPref, setMarkingPref] = useState(() => readFileSideWidth(user?.id, 'marking'));
  const [filesSheetOpen, setFilesSheetOpen] = useState(false);
  const [flowAnchors, setFlowAnchors] = useState<{ id: string; y: number }[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [filter, setFilter] = useState<HighlightFilter>('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [zoomMode, setZoomMode] = useState<'fit' | number>('fit');
  const [liveZoom, setLiveZoom] = useState(1);
  const [committedZoom, setCommittedZoom] = useState(1);
  const pageWidth100Ref = useRef(0);
  const [pageMetrics, setPageMetrics] = useState<PdfPageMetric[]>([]);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [highlightsVisible, setHighlightsVisible] = useState(() => readHighlightsVisible(user?.id));
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [discardKind, setDiscardKind] = useState<'draft' | 'edit' | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [railTab, setRailTab] = useState<RailTab>('markings');
  const [infoSeed, setInfoSeed] = useState<DocumentInfoSeed | null>(null);

  const filename = view.type === 'document' ? view.filename : view.file.name;
  const declaredMime =
    view.type === 'document'
      ? resolveStoredDocumentMimeType({
          mimeType: view.mimeType,
          filename: view.filename,
        })
      : resolveFileMimeType(view.file);
  const [mimeType, setMimeType] = useState(declaredMime);
  const kind = classifyPreview(mimeType, filename);

  useEffect(() => {
    let objectUrl: string | null = null;
    let actionObjectUrl: string | null = null;
    let cancelled = false;

    function notePreviewFailure(error: unknown) {
      if (cancelled) return;
      setInlineFailed(true);
      if (error instanceof Error && error.message === 'preview_timeout') setTimedOut(true);
    }

    async function publishOriginal(blob: Blob) {
      const type = blob.type || declaredMime;
      const url = URL.createObjectURL(blob);
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      objectUrl = url;
      setMimeType(type);
      setPreviewBlob(blob);
      setPreviewUrl(url);
      setActionUrl(url);
      const nextKind = classifyPreview(type, filename);
      if (nextKind !== 'text' && nextKind !== 'csv' && nextKind !== 'sheet') return;
      try {
        const parsed = await withTimeout(readPreviewBody(nextKind, await blob.arrayBuffer()));
        if (!cancelled) setContent(parsed);
      } catch (error) {
        notePreviewFailure(error);
      }
    }

    async function publishDocx(blob: Blob) {
      const originalUrl = URL.createObjectURL(blob);
      if (cancelled) {
        URL.revokeObjectURL(originalUrl);
        return;
      }
      actionObjectUrl = originalUrl;
      setMimeType(blob.type || declaredMime);
      setPreviewBlob(blob);
      setActionUrl(originalUrl);
      try {
        const html = await withTimeout(docxPreviewHtml(await blob.arrayBuffer()));
        if (!cancelled) setDocxHtml(html);
      } catch (error) {
        notePreviewFailure(error);
      }
    }

    async function load() {
      setLoading(true);
      setError(false);
      setUnavailable(false);
      setInlineFailed(false);
      setTimedOut(false);
      setPreviewUrl(null);
      setPreviewBlob(null);
      setContent(null);
      setDocxHtml(null);
      setActionUrl(null);
      setMimeType(declaredMime);
      if (zoomSourceRef.current !== view) {
        zoomSourceRef.current = view;
        pendingFlowAnchor.current = null;
        setZoomMode('fit');
        setLiveZoom(1);
        setCommittedZoom(1);
      }
      setDraft(null);
      setSelectedId(view.type === 'document' ? (view.highlightId ?? null) : null);
      setEditingId(null);
      setInfoSeed(null);
      if (railResetKey.current !== externalKey) {
        railResetKey.current = externalKey;
        setRailTab('markings');
      }

      try {
        if (view.type === 'local') {
          const blob = previewBlobFromFile(view.file, declaredMime);
          if (isWordDocumentMime(blob.type || declaredMime)) {
            await publishDocx(blob);
            return;
          }
          await publishOriginal(blob);
          return;
        }

        const signed = await fetchDocumentUrl(view.documentId);
        if (cancelled) return;

        try {
          const blob = await previewBlobFromResponse(await fetch(signed), {
            mimeType: view.mimeType,
            filename,
          });
          if (cancelled) return;

          if (isWordDocumentMime(blob.type || declaredMime)) {
            await publishDocx(blob);
            return;
          }

          await publishOriginal(blob);
        } catch {
          if (cancelled) return;
          if (
            declaredMime.startsWith('image/') &&
            classifyPreview(declaredMime, filename) === 'image'
          ) {
            setActionUrl(signed);
            setPreviewUrl(signed);
          } else {
            setActionUrl(signed);
            setInlineFailed(true);
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          setUnavailable(isDocumentStorageAccessError(loadError));
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (actionObjectUrl && actionObjectUrl !== objectUrl) URL.revokeObjectURL(actionObjectUrl);
    };
  }, [view, externalKey, fetchDocumentUrl, declaredMime, filename]);

  const authorNameByUserId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const member of members ?? []) {
      map[member.user_id] = member.name;
    }
    return map;
  }, [members]);

  const numbered = useMemo(
    () => numberHighlights(highlightsQuery.data ?? []),
    [highlightsQuery.data],
  );
  useLayoutEffect(() => {
    if (panelRestoreKey.current === externalKey) return;
    panelRestoreKey.current = externalKey;
    setPanelOpen(readMarkingPanelOpen(user?.id));
    setFilesOpen(readFilesPanelOpen(user?.id));
  }, [externalKey, user?.id]);
  useEffect(() => {
    setFilesPref(readFileSideWidth(user?.id, 'files'));
    setMarkingPref(readFileSideWidth(user?.id, 'marking'));
  }, [user?.id]);
  useEffect(() => {
    const syncViewport = () => setViewportWidth(window.innerWidth);
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);
  const initialHighlightId = view.type === 'document' ? (view.highlightId ?? null) : null;
  useEffect(() => {
    if (!initialHighlightId) {
      openedHighlightRef.current = null;
      return;
    }
    if (openedHighlightRef.current === initialHighlightId) return;
    const row = numbered.find((item) => item.id === initialHighlightId);
    if (!row) return;
    openedHighlightRef.current = initialHighlightId;
    setSelectedId(row.id);
    setScrollToPage(row.page);
    setPanelOpen(true);
    setSheetOpen(true);
    setFilesSheetOpen(false);
  }, [initialHighlightId, numbered]);
  const filtered = useMemo(
    () => filterNumberedHighlights(numbered, { filter, query, userId: user?.id }),
    [filter, numbered, query, user?.id],
  );
  const counts = useMemo(() => highlightFilterCounts(numbered, user?.id), [numbered, user?.id]);
  const selected = numbered.find((row) => row.id === selectedId) ?? null;
  const persistable = Boolean(documentId && previewDoc.data && user?.id);
  const admin = clinicId ? isClinicAdmin(clinicId) : false;
  const showPdf =
    Boolean(previewBlob) && mimeType === 'application/pdf' && !loading && !error && !inlineFailed;
  const showImage = kind === 'image' && Boolean(previewUrl) && !loading && !error && !inlineFailed;
  const showVideo = kind === 'video' && Boolean(previewUrl) && !loading && !error && !inlineFailed;
  const showText =
    kind === 'text' && content?.kind === 'text' && !loading && !error && !inlineFailed;
  const showDocx = kind === 'word' && Boolean(docxHtml) && !loading && !error && !inlineFailed;
  const showTable =
    (kind === 'csv' || kind === 'sheet') &&
    content?.kind === 'table' &&
    !loading &&
    !error &&
    !inlineFailed;
  const showSheet = kind === 'sheet' && showTable;
  const showFlow = showText || showDocx || showTable;
  const showFallback =
    !loading &&
    !error &&
    Boolean(actionUrl) &&
    !showPdf &&
    !showImage &&
    !showVideo &&
    !showText &&
    !showDocx &&
    !showTable;
  const canOpenInNewTab = Boolean(previewUrl || actionUrl);
  const showPanel = Boolean(documentId && previewDoc.data);
  // Filer stays up while the next file's metadata loads. Tying it to showPanel
  // unmounted the list on every click and cleared the tag filter and search.
  const showFilesTool =
    view.type === 'document' &&
    (Boolean(previewDoc.data) ||
      browseIds.current.clinicId != null ||
      browseIds.current.patientId != null);
  const filesPresence: FileSidePresence =
    !desktop || !showFilesTool ? 'hidden' : filesOpen ? 'open' : 'rail';
  const markingPresence: FileSidePresence =
    !desktop || !showPanel ? 'hidden' : panelOpen ? 'open' : 'rail';
  const sideWidths = useMemo(
    () =>
      displayedFileSideWidths({
        filesPref,
        markingPref,
        viewportWidth,
        files: filesPresence,
        marking: markingPresence,
      }),
    [filesPref, filesPresence, markingPref, markingPresence, viewportWidth],
  );
  const filesOccupied = fileSideOccupied(filesPresence, sideWidths.files);
  const markingOccupied = fileSideOccupied(markingPresence, sideWidths.marking);
  const applyFilesWidth = (next: number) => {
    const width = clampFileSideWidth(next, viewportWidth, markingOccupied);
    setFilesPref(width);
    writeFileSideWidth(user?.id, 'files', width);
  };
  const applyMarkingWidth = (next: number) => {
    const width = clampFileSideWidth(next, viewportWidth, filesOccupied);
    setMarkingPref(width);
    writeFileSideWidth(user?.id, 'marking', width);
  };
  const zoomPercent = Math.round(liveZoom * 100);
  const highlightIndex = selected ? filtered.findIndex((row) => row.id === selected.id) + 1 : null;
  const documentHighlights = highlightsVisible
    ? filtered
    : selected
      ? filtered.filter((row) => row.id === selected.id)
      : [];
  const showMarginRail = persistable && (showPdf || showText || showDocx || showSheet);
  const flowMarkKey = documentHighlights
    .map((highlight) => `${highlight.id}:${highlight.color}`)
    .join('|');

  const measureFlowRings = useCallback(() => {
    if (showPdf) return;
    const scroll = scrollRef.current;
    const root = docxRef.current;
    if (!scroll || !root) return;
    const host = markingScrollHost(scroll);
    const next = marginRingOffsets(root, host);
    setFlowAnchors((current) =>
      current.length === next.length &&
      current.every((item, index) => item.id === next[index]?.id && item.y === next[index]?.y)
        ? current
        : next,
    );
    setScrollHeight((current) => (current === host.scrollHeight ? current : host.scrollHeight));
    if (railInnerRef.current) {
      railInnerRef.current.style.transform = `translateY(${-host.scrollTop}px)`;
    }
  }, [showPdf]);

  useEffect(() => {
    if (!showMarginRail || showPdf) return;
    const scroll = scrollRef.current;
    if (!scroll) return;
    const host = markingScrollHost(scroll);
    const onHostScroll = () => {
      if (railInnerRef.current) {
        railInnerRef.current.style.transform = `translateY(${-host.scrollTop}px)`;
      }
    };
    host.addEventListener('scroll', onHostScroll, { passive: true });
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measureFlowRings());
    observer?.observe(host);
    const root = docxRef.current;
    if (root && root !== host) observer?.observe(root);
    return () => {
      host.removeEventListener('scroll', onHostScroll);
      observer?.disconnect();
    };
  }, [content, docxHtml, measureFlowRings, showMarginRail, showPdf]);

  useEffect(() => {
    const timer = window.setTimeout(() => setCommittedZoom(liveZoom), 160);
    return () => window.clearTimeout(timer);
  }, [liveZoom]);

  const applyZoom = useCallback(
    (next: number, origin?: { x: number; y: number }) => {
      const prev = liveZoom;
      const clamped = clampHighlightZoom(next);
      setZoomMode(clamped);
      setLiveZoom(clamped);
      if (!origin || !(prev > 0)) return;
      const ratio = clamped / prev;
      if (ratio === 1) return;
      const outer = scrollRef.current;
      if (!outer) return;
      const host = markingScrollHost(outer);
      // PDF and images scroll the outer pane, which is already the right size
      // to anchor against. Text, Word, and sheets grow in the inner scrollport
      // during layout, so the anchor is applied after that resize.
      if (host === outer) {
        applyAnchoredScroll(host, origin, ratio);
        return;
      }
      pendingFlowAnchor.current = { origin, ratio };
    },
    [liveZoom],
  );

  useLayoutEffect(() => {
    const pending = pendingFlowAnchor.current;
    if (!pending) return;
    pendingFlowAnchor.current = null;
    const outer = scrollRef.current;
    if (!outer) return;
    const host = markingScrollHost(outer);
    if (host === outer) return;
    applyAnchoredScroll(host, pending.origin, pending.ratio);
  }, [liveZoom]);

  const showDocChrome = showPdf || showImage || showFlow;
  useLayoutEffect(() => {
    const doc = docRef.current;
    const scroll = scrollRef.current;
    if (!doc || !scroll || !showDocChrome) {
      doc?.style.removeProperty('--fp-chrome-left');
      doc?.style.removeProperty('--fp-chrome-width');
      return;
    }

    const apply = () => syncPreviewChrome(doc, scroll);
    let watched: HTMLElement | null = null;
    let observer: ResizeObserver | null = null;
    const watchDocument = () => {
      if (!observer) return;
      const pages = scroll.querySelector<HTMLElement>('.fp-pages');
      if (pages) observer.observe(pages);
      const content = scroll.querySelector<HTMLElement>(
        '.fp-page, .fp-skel, .fp-text-view, .fp-docx, .fp-sheet, .fp-media img, .fp-media video',
      );
      if (!content || content === watched) return;
      if (watched) observer.unobserve(watched);
      watched = content;
      observer.observe(content);
    };
    observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            watchDocument();
            apply();
          });
    observer?.observe(scroll);
    watchDocument();
    apply();
    scroll.addEventListener('scroll', apply, { passive: true });
    return () => {
      observer?.disconnect();
      scroll.removeEventListener('scroll', apply);
    };
  }, [showDocChrome]);

  // PDF page boxes are sized in a child effect. Measure again after that,
  // so a zoom step does not leave the bar on the previous page width.
  useEffect(() => {
    const doc = docRef.current;
    const scroll = scrollRef.current;
    if (!doc || !scroll || !showDocChrome) return;
    syncPreviewChrome(doc, scroll);
  }, [liveZoom, showDocChrome]);

  const applyFit = useCallback(() => {
    pendingFlowAnchor.current = null;
    const host = scrollRef.current;
    const nextFit = fitHighlightZoom(
      host?.clientWidth ?? 0,
      pageWidth100Ref.current,
      pageSideMarginPx(host),
    );
    setZoomMode('fit');
    setLiveZoom(nextFit);
    setCommittedZoom(nextFit);
  }, []);

  const applyImageFit = useCallback(() => {
    pendingFlowAnchor.current = null;
    setZoomMode('fit');
    setLiveZoom(1);
    setCommittedZoom(1);
  }, []);

  const applyFlowFit = useCallback(() => {
    pendingFlowAnchor.current = null;
    const outer = scrollRef.current;
    const root = docxRef.current;
    let next = 1;
    if (outer && root) {
      const host = markingScrollHost(outer);
      const table = root.querySelector('table');
      if (table instanceof HTMLElement && root.closest('.fp-sheet')) {
        next = flowFitZoom(flowContentBoxWidth(host), table.scrollWidth);
      }
    }
    setZoomMode('fit');
    setLiveZoom((current) => (sameHighlightZoom(current, next) ? current : next));
    setCommittedZoom((current) => (sameHighlightZoom(current, next) ? current : next));
  }, []);

  useLayoutEffect(() => {
    if (!showFlow || zoomMode !== 'fit') return;
    applyFlowFit();
  }, [applyFlowFit, content, docxHtml, filesOpen, panelOpen, showFlow, zoomMode]);

  useEffect(() => {
    if (!showFlow || zoomMode !== 'fit') return;
    const outer = scrollRef.current;
    if (!outer || typeof ResizeObserver === 'undefined') return;
    const host = markingScrollHost(outer);
    const observer = new ResizeObserver(() => applyFlowFit());
    observer.observe(host);
    return () => observer.disconnect();
  }, [applyFlowFit, content, docxHtml, showFlow, zoomMode]);

  const panelFitReady = useRef(false);
  useEffect(() => {
    if (!showPdf || zoomMode !== 'fit') return;
    if (!panelFitReady.current) {
      panelFitReady.current = true;
      return;
    }
    const frame = window.requestAnimationFrame(() => applyFit());
    return () => window.cancelAnimationFrame(frame);
  }, [applyFit, filesOpen, panelOpen, showPdf, zoomMode]);

  const toggleHighlights = useCallback(() => {
    if (!persistable) return;
    setHighlightsVisible((value) => {
      const next = !value;
      writeHighlightsVisible(user?.id, next);
      return next;
    });
  }, [persistable, user?.id]);

  useFilePreviewZoom({
    enabled: showPdf || showImage || showFlow,
    zoom: liveZoom,
    scrollRef,
    onZoom: applyZoom,
    onFit: showPdf ? applyFit : showImage ? applyImageFit : applyFlowFit,
    onToggleHighlights: showPdf && persistable ? toggleHighlights : undefined,
  });

  useEffect(() => {
    if (toolbarTagTouched.current) return;
    const first = activeHighlightTags(highlightTags)[0];
    if (!first) return;
    toolbarTagTouched.current = true;
    setToolbarTagIds([first.id]);
    const color = highlightColorForTagToken(first.color_token);
    if (color) setToolbarColor(color);
  }, [highlightTags]);

  const applyToolbarTags = useCallback(
    (ids: string[]) => {
      toolbarTagTouched.current = true;
      setToolbarTagIds(ids);
      const color = highlightColorForSelectedTags(highlightTags, ids);
      if (color) setToolbarColor(color);
    },
    [highlightTags],
  );

  const applyEditTags = useCallback(
    (ids: string[]) => {
      setEditTagIds(ids);
      const color = highlightColorForSelectedTags(highlightTags, ids);
      if (color) setEditColor(color);
    },
    [highlightTags],
  );

  const clearDraft = useCallback(() => {
    const first = activeHighlightTags(highlightTags)[0];
    const color = first ? highlightColorForTagToken(first.color_token) : null;
    setDraft(null);
    setMultiPageHint(false);
    setToolbarComment('');
    setToolbarTagIds(first ? [first.id] : []);
    setToolbarColor(color ?? 'yellow');
    setToolbarVisibility('clinic');
    setSaveError(null);
    window.getSelection()?.removeAllRanges();
  }, [highlightTags]);

  const requestClearDraft = useCallback(() => {
    if (draft && toolbarComment.trim()) {
      setDiscardKind('draft');
      return;
    }
    clearDraft();
  }, [clearDraft, draft, toolbarComment]);

  const requestClearEdit = useCallback(() => {
    const row = numbered.find((item) => item.id === editingId);
    if (
      row &&
      (editComment !== (row.comment ?? '') ||
        editColor !== row.color ||
        editVisibility !== row.visibility ||
        !sameHighlightTagIds(editTagIds, row.tagIds ?? []))
    ) {
      setDiscardKind('edit');
      return;
    }
    setEditingId(null);
    setEditError(null);
  }, [editColor, editComment, editTagIds, editVisibility, editingId, numbered]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (menuOpen || document.querySelector('[data-fp-menu] [role="menu"], [role="menu"]')) {
        setMenuOpen(false);
        return;
      }
      if (discardKind || deleteId) return;
      if (editingId) {
        event.preventDefault();
        requestClearEdit();
        return;
      }
      if (draft) {
        event.preventDefault();
        requestClearDraft();
        return;
      }
      if (selectedId) {
        event.preventDefault();
        setSelectedId(null);
        return;
      }
      if (sheetOpen) {
        event.preventDefault();
        setSheetOpen(false);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    deleteId,
    discardKind,
    draft,
    editingId,
    menuOpen,
    onClose,
    requestClearDraft,
    requestClearEdit,
    selectedId,
    sheetOpen,
  ]);

  const syncTags = useCallback(
    async (highlightId: string, clinicIdForTags: string, tagIds: readonly string[]) => {
      if (!documentId || !highlightTagsQuery.isSuccess) return;
      try {
        await setHighlightTags.mutateAsync({
          highlightId,
          documentId,
          clinicId: clinicIdForTags,
          tagIds,
        });
      } catch {
        toast.error(t('documents.highlights.tag_save_failed'));
      }
    },
    [documentId, highlightTagsQuery.isSuccess, setHighlightTags, t],
  );

  const persistHighlight = useCallback(async () => {
    if (!persistable || !previewDoc.data || !user?.id || !draft) return;
    setSaveError(null);
    try {
      const row = await createHighlight.mutateAsync({
        clinicId: previewDoc.data.clinic_id,
        documentId: previewDoc.data.id,
        storagePath: previewDoc.data.storage_path,
        createdBy: user.id,
        page: draft.page,
        quote: draft.quote,
        prefix: draft.prefix,
        suffix: draft.suffix,
        rects: draft.rects,
        textAnchor: draft.textAnchor,
        cellAnchor: draft.cellAnchor,
        color: toolbarColor,
        comment: toolbarComment.trim() || null,
        visibility: toolbarVisibility,
      });
      await syncTags(row.id, row.clinic_id, toolbarTagIds);
      window.getSelection()?.removeAllRanges();
      setDraft(null);
      setMultiPageHint(false);
      setToolbarComment('');
      setSelectedId(row.id);
      setScrollToPage(row.page);
      setHighlightsVisible(true);
      writeHighlightsVisible(user.id, true);
    } catch {
      setSaveError(t('documents.highlights.save_failed'));
    }
  }, [
    createHighlight,
    draft,
    persistable,
    previewDoc.data,
    syncTags,
    t,
    toolbarColor,
    toolbarComment,
    toolbarTagIds,
    toolbarVisibility,
    user?.id,
  ]);

  const handleDownload = () => {
    const run = async () => {
      if (previewBlob) {
        await downloadBytesAsFile({
          bytes: await previewBlob.arrayBuffer(),
          mimeType: previewBlob.type || declaredMime,
          filename,
        });
        return;
      }
      if (!actionUrl) {
        throw unavailable ? new DocumentStorageAccessError() : new Error('download_failed');
      }
      await downloadUrlAsFile({ url: actionUrl, mimeType: declaredMime, filename });
    };
    void run().catch((downloadError: unknown) => {
      toast.error(t(documentAccessFailureKey(downloadError)));
    });
  };

  const handleOpenInNewTab = () => {
    const url = previewUrl ?? actionUrl;
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadMarked = async () => {
    if (!previewBlob || previewBlob.type !== 'application/pdf') return;
    setExporting(true);
    try {
      const bytes = await embedHighlightsInPdfCopy({
        pdfBytes: await previewBlob.arrayBuffer(),
        highlights: numbered,
        authorNameByUserId,
      });
      const buffer = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(buffer).set(bytes);
      const url = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = markedDownloadFilename(filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('documents.highlights.pdfError'));
    } finally {
      setExporting(false);
    }
  };

  const handleTextSelected = useCallback(
    (analysis: HighlightSelectionAnalysis) => {
      if (!persistable) return;
      switch (analysis.type) {
        case 'none':
          return;
        case 'multi-page':
          justSelectedRef.current = true;
          setDraft(null);
          setSelectedId(null);
          setEditingId(null);
          setMultiPageHint(true);
          setPanelOpen(true);
          if (!desktop) setSheetOpen(true);
          return;
        case 'draft':
          justSelectedRef.current = true;
          setSelectedId(null);
          setEditingId(null);
          setMultiPageHint(false);
          setSaveError(null);
          setPanelOpen(true);
          setDraft((prev) =>
            prev && prev.page === analysis.draft.page && prev.quote === analysis.draft.quote
              ? prev
              : analysis.draft,
          );
          if (!desktop) setSheetOpen(true);
          return;
        default: {
          const _never: never = analysis;
          return _never;
        }
      }
    },
    [desktop, persistable],
  );

  const handleDocxTextSelected = useCallback(
    (analysis: HighlightSelectionAnalysis) => {
      handleTextSelected(analysis);
    },
    [handleTextSelected],
  );

  useEffect(() => {
    if (!showDocx && !showSheet && !showText) return;
    const host = scrollRef.current;
    if (!host) return;
    return bindHighlightSelection(host, handleDocxTextSelected);
  }, [handleDocxTextSelected, showDocx, showSheet, showText]);

  useLayoutEffect(() => {
    const root = docxRef.current;
    if ((!showDocx && !showText) || !root) return;
    syncHtmlHighlightMarks(
      root,
      documentHighlights.map((highlight) => ({
        id: highlight.id,
        quote: highlight.quote,
        prefix: highlight.prefix,
        suffix: highlight.suffix,
        textAnchor: highlight.textAnchor,
        color: highlight.color,
        selected: highlight.id === selectedId,
        hovered: highlight.id === hoveredId,
      })),
    );
  }, [content, documentHighlights, docxHtml, hoveredId, liveZoom, selectedId, showDocx, showText]);

  useLayoutEffect(() => {
    const root = docxRef.current;
    if (!showSheet || !root) return;
    syncSheetHighlightMarks(
      root,
      documentHighlights.map((highlight) => ({
        id: highlight.id,
        cellAnchor: highlight.cellAnchor,
        color: highlight.color,
        selected: highlight.id === selectedId,
        hovered: highlight.id === hoveredId,
      })),
    );
  }, [content, documentHighlights, hoveredId, liveZoom, selectedId, showSheet]);

  useLayoutEffect(() => {
    if (!showMarginRail || showPdf) return;
    measureFlowRings();
  }, [
    content,
    docxHtml,
    flowMarkKey,
    liveZoom,
    measureFlowRings,
    showDocx,
    showMarginRail,
    showPdf,
    showSheet,
    showText,
  ]);

  useEffect(() => {
    if ((!showDocx && !showText && !showSheet) || !selectedId) return;
    const root = docxRef.current;
    const scroll = scrollRef.current;
    if (!root || !scroll) return;
    const mark = [...root.querySelectorAll<HTMLElement>('[data-hl-id]')].find(
      (node) => node.dataset.hlId === selectedId,
    );
    if (mark instanceof HTMLElement) assignScrollTopToReveal(markingScrollHost(scroll), mark);
  }, [content, documentHighlights, docxHtml, selectedId, showDocx, showSheet, showText]);

  const handleSelectHighlight = (id: string | null) => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (id == null) {
      if (draft) requestClearDraft();
      setSelectedId(null);
      return;
    }
    setDraft(null);
    setMultiPageHint(false);
    setEditingId(null);
    setSelectedId(id);
    setRailTab('markings');
    const row = numbered.find((item) => item.id === id);
    if (row) setScrollToPage(row.page);
    setPanelOpen(true);
    if (!desktop) {
      setSheetOpen(true);
      setFilesSheetOpen(false);
    }
  };

  const handleEdit = (id: string) => {
    const row = numbered.find((item) => item.id === id);
    if (!row) return;
    setSelectedId(id);
    setEditingId(id);
    setEditColor(row.color);
    setEditTagIds(row.tagIds ?? []);
    setEditVisibility(row.visibility);
    setEditComment(row.comment ?? '');
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !documentId) return;
    setEditError(null);
    try {
      await updateHighlight.mutateAsync({
        id: editingId,
        documentId,
        color: editColor,
        comment: editComment.trim() || null,
        visibility: editVisibility,
      });
      const previous = numbered.find((item) => item.id === editingId);
      if (previous) {
        await syncTags(editingId, previous.clinic_id, editTagIds);
      }
      setEditingId(null);
    } catch {
      setEditError(t('documents.highlights.save_failed'));
    }
  };

  const handleToggleVisibility = async (id: string, visibility: HighlightVisibility) => {
    if (!documentId) return;
    try {
      await updateHighlight.mutateAsync({ id, documentId, visibility });
    } catch {
      toast.error(t('documents.highlights.save_failed'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId || !documentId) return;
    try {
      await deleteHighlight.mutateAsync({ id: deleteId, documentId });
      if (selectedId === deleteId) setSelectedId(null);
      if (editingId === deleteId) setEditingId(null);
      setDeleteId(null);
    } catch {
      toast.error(t('documents.highlights.delete_failed'));
    }
  };

  const moveHighlight = (delta: number) => {
    if (filtered.length === 0) return;
    const current = selectedId ? filtered.findIndex((row) => row.id === selectedId) : -1;
    const next =
      current < 0
        ? delta > 0
          ? 0
          : filtered.length - 1
        : (current + delta + filtered.length) % filtered.length;
    handleSelectHighlight(filtered[next].id);
  };

  useEffect(() => {
    if (!showPdf) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.closest('input, textarea, [contenteditable="true"]') ||
          target.closest('.fp-list-body, .fp-files'))
      ) {
        return;
      }
      if (filtered.length === 0) return;
      event.preventDefault();
      moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtered, selectedId, showPdf]);

  const onGeometry = useCallback(
    (info: { hostWidth: number; pageWidth100: number; pageCount: number }) => {
      pageWidth100Ref.current = info.pageWidth100;
      const nextFit = fitHighlightZoom(
        info.hostWidth,
        info.pageWidth100,
        pageSideMarginPx(scrollRef.current),
      );
      setPageCount(info.pageCount);
      setZoomMode((mode) => {
        if (mode === 'fit') {
          setLiveZoom((current) => (sameHighlightZoom(current, nextFit) ? current : nextFit));
          setCommittedZoom((current) => (sameHighlightZoom(current, nextFit) ? current : nextFit));
        }
        return mode;
      });
    },
    [],
  );

  const onMetrics = useCallback((info: { pages: PdfPageMetric[]; scrollHeight: number }) => {
    setPageMetrics(info.pages);
    setScrollHeight(info.scrollHeight);
  }, []);

  const onScroll = () => {
    if (!showPdf) return;
    const top = scrollRef.current?.scrollTop ?? 0;
    if (railInnerRef.current) railInnerRef.current.style.transform = `translateY(${-top}px)`;
  };

  const rememberPanel = (open: boolean) => {
    setPanelOpen(open);
    writeMarkingPanelOpen(user?.id, open);
  };

  const rememberFiles = (open: boolean) => {
    setFilesOpen(open);
    writeFilesPanelOpen(user?.id, open);
  };

  const setVisible = (visible: boolean) => {
    setHighlightsVisible(visible);
    writeHighlightsVisible(user?.id, visible);
  };

  const kindLabelKey = isWordDocumentMime(declaredMime) ? null : previewKindLabelKey(kind);
  const fileKind = isWordDocumentMime(declaredMime)
    ? t('documents.highlights.fileKindWord')
    : kindLabelKey
      ? t(kindLabelKey)
      : mimeType;
  const subtitle = [
    fileKind,
    pageCount > 0 ? t('documents.highlights.pages', { count: pageCount }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const tableNote = (() => {
    if (content?.kind !== 'table') return null;
    const parts: string[] = [];
    if (content.sheetCount > 1 && content.title) {
      parts.push(
        t('documents.preview.first_sheet', {
          name: content.title,
          count: content.sheetCount,
        }),
      );
    }
    if (content.truncated) parts.push(t('documents.preview.truncated'));
    return parts.length > 0 ? parts.join(' ') : null;
  })();

  const stage: FilePreviewStageState | null = showPdf
    ? null
    : loading
      ? { type: 'loading', message: null }
      : error
        ? {
            type: 'error',
            message: t(unavailable ? 'documents.file_unavailable' : 'documents.preview_failed'),
            onDownload: handleDownload,
          }
        : showImage && previewUrl
          ? {
              type: 'image',
              url: previewUrl,
              fit: zoomMode === 'fit',
              zoom: liveZoom,
            }
          : showVideo && previewUrl
            ? { type: 'video', url: previewUrl }
            : showText && content?.kind === 'text'
              ? {
                  type: 'text',
                  text: content.text,
                  truncated: content.truncated,
                }
              : showDocx && docxHtml
                ? { type: 'html', html: docxHtml }
                : showTable && content?.kind === 'table'
                  ? {
                      type: 'table',
                      rows: content.rows,
                      note: tableNote,
                      sheetName: kind === 'sheet' ? (content.title ?? 'Ark') : null,
                      excelRows: content.excelRows ?? null,
                    }
                  : showFallback
                    ? {
                        type: 'fallback',
                        message: timedOut
                          ? t('documents.preview.timed_out')
                          : kind === 'none'
                            ? t('documents.no_preview')
                            : t('documents.preview_failed'),
                        canOpen: canOpenInNewTab,
                        onOpen: handleOpenInNewTab,
                        onDownload: handleDownload,
                      }
                    : null;

  const downloadMenu = (
    <MenuPopover
      align="end"
      title={t('documents.highlights.download')}
      className="w-[300px]"
      trigger={
        <Button
          type="button"
          variant="secondary"
          loading={exporting}
          loadingText={t('documents.highlights.preparingPdf')}
          onClick={() => setMenuOpen(true)}
        >
          <Download />
          {exporting ? t('documents.highlights.preparingPdf') : t('documents.highlights.download')}
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      }
    >
      <MenuItem
        icon={<Download className="h-4 w-4" />}
        description={t('documents.highlights.downloadOriginalHint')}
        onClick={() => {
          setMenuOpen(false);
          handleDownload();
        }}
      >
        {t('documents.highlights.downloadOriginal')}
      </MenuItem>
      {showPdf ? (
        <MenuItem
          icon={<Highlighter className="h-4 w-4" />}
          description={t('documents.highlights.downloadWithHighlightsHint')}
          onClick={() => {
            setMenuOpen(false);
            void handleDownloadMarked();
          }}
        >
          {t('documents.highlights.downloadWithHighlights')}
        </MenuItem>
      ) : null}
    </MenuPopover>
  );

  const collapseMarking = () => {
    if (desktop) rememberPanel(false);
    else setSheetOpen(false);
  };

  const openPatientFile = (doc: DocumentRow) => {
    if (view.type === 'document' && view.documentId === doc.id) return;
    setBrowsed({
      type: 'document',
      documentId: doc.id,
      filename: doc.original_filename,
      mimeType: doc.mime_type,
    });
  };

  const assignSelectionToDocumentInfo = (field: DocumentInfoField) => {
    if (!draft) return;
    infoSeedId.current += 1;
    setInfoSeed({ field, quote: draft.quote, id: infoSeedId.current });
    setRailTab('info');
    setPanelOpen(true);
    if (!desktop) {
      setSheetOpen(true);
      setFilesSheetOpen(false);
    }
  };

  const collapseFiles = () => {
    if (desktop) rememberFiles(false);
    else setFilesSheetOpen(false);
  };

  const panel = showPanel ? (
    <aside className="fp-panel" data-fp-panel data-testid="file-marking-panel">
      <button
        type="button"
        className="fp-panel-head"
        aria-expanded={desktop || sheetOpen}
        title={t('documents.highlights.collapsePanel')}
        onClick={collapseMarking}
      >
        <h2>{t('documents.highlights.panel_title')}</h2>
        <ChevronRight className="h-4 w-4" strokeWidth={1.75} aria-hidden />
      </button>
      <div
        className="fp-panel-tabs"
        role="tablist"
        aria-label={t('documents.highlights.panel_title')}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          const tabs: RailTab[] = ['markings', 'info'];
          const index = Math.max(0, tabs.indexOf(railTab));
          event.preventDefault();
          const next =
            event.key === 'ArrowRight'
              ? tabs[(index + 1) % tabs.length]
              : tabs[(index - 1 + tabs.length) % tabs.length];
          if (!next) return;
          setRailTab(next);
          const tabId = next === 'markings' ? 'fp-rail-tab-markings' : 'fp-rail-tab-info';
          document.getElementById(tabId)?.focus();
        }}
      >
        <button
          type="button"
          role="tab"
          id="fp-rail-tab-markings"
          aria-controls="fp-rail-markings-panel"
          aria-selected={railTab === 'markings'}
          tabIndex={railTab === 'markings' ? 0 : -1}
          data-testid="file-marking-tab-markings"
          onClick={() => setRailTab('markings')}
        >
          {t('documents.highlights.list_title')}
        </button>
        <button
          type="button"
          role="tab"
          id="fp-rail-tab-info"
          aria-controls="fp-rail-info-panel"
          aria-selected={railTab === 'info'}
          tabIndex={railTab === 'info' ? 0 : -1}
          data-testid="file-marking-tab-info"
          onClick={() => setRailTab('info')}
        >
          {t('documents.info.tab')}
        </button>
      </div>
      {railTab === 'info' && documentId ? (
        <FileDocumentInfo
          key={documentId}
          documentId={documentId}
          selectionQuote={draft?.quote ?? null}
          seed={infoSeed}
          onAssign={assignSelectionToDocumentInfo}
        />
      ) : (
        <div
          className="fp-rail-markings"
          role="tabpanel"
          id="fp-rail-markings-panel"
          aria-labelledby="fp-rail-tab-markings"
        >
          <FileHighlightToolbar
            draft={persistable ? draft : null}
            multiPageHint={multiPageHint}
            color={toolbarColor}
            visibility={toolbarVisibility}
            comment={toolbarComment}
            tags={highlightTags}
            selectedTagIds={toolbarTagIds}
            saving={createHighlight.isPending}
            error={saveError}
            onColor={setToolbarColor}
            onSelectedTagIds={applyToolbarTags}
            onVisibility={setToolbarVisibility}
            onComment={setToolbarComment}
            onSave={() => void persistHighlight()}
            onCancel={requestClearDraft}
          />
          <FileHighlightList
            highlights={numbered}
            filtered={filtered}
            filter={filter}
            query={query}
            counts={counts}
            selectedId={selectedId}
            hoveredId={hoveredId}
            editingId={editingId}
            userId={user?.id}
            isClinicAdmin={admin}
            authorNameByUserId={authorNameByUserId}
            loading={highlightsQuery.isLoading}
            loadError={highlightsQuery.isError}
            hiddenNotice={!highlightsVisible}
            editColor={editColor}
            editVisibility={editVisibility}
            editComment={editComment}
            highlightTags={highlightTags}
            editTagIds={editTagIds}
            saving={updateHighlight.isPending}
            saveError={editError}
            onFilter={setFilter}
            onQuery={setQuery}
            onClearFilter={() => {
              setFilter('all');
              setQuery('');
            }}
            onSelect={handleSelectHighlight}
            onHover={setHoveredId}
            onMenuOpen={setMenuOpen}
            onEdit={handleEdit}
            onCancelEdit={requestClearEdit}
            onSaveEdit={() => void handleSaveEdit()}
            onToggleVisibility={(id, visibility) => void handleToggleVisibility(id, visibility)}
            onAskDelete={setDeleteId}
            onEditColor={setEditColor}
            onEditTagIds={applyEditTags}
            onEditVisibility={setEditVisibility}
            onEditComment={setEditComment}
            onShowHighlights={() => setVisible(true)}
            onRetry={() => void highlightsQuery.refetch()}
          />
        </div>
      )}
      {desktop ? (
        <FileSideResizeHandle
          side="marking"
          value={sideWidths.marking}
          min={FILE_SIDE_WIDTH_MIN}
          max={maxFileSideWidth(viewportWidth, filesOccupied)}
          label={t('documents.highlights.resize')}
          onChange={applyMarkingWidth}
          onReset={() => applyMarkingWidth(FILE_SIDE_WIDTH_DEFAULT)}
        />
      ) : null}
    </aside>
  ) : null;

  const filesPanel = showFilesTool ? (
    <aside
      className="fp-files-col"
      data-testid="file-browser-panel"
      aria-label={t('documents.browser.tab')}
    >
      <button
        type="button"
        className="fp-files-head"
        aria-expanded={desktop || filesSheetOpen}
        title={t('documents.browser.collapse')}
        onClick={collapseFiles}
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        <h2>{t('documents.browser.tab')}</h2>
      </button>
      <FilePreviewBrowser
        clinicId={browseClinicId}
        patientId={browsePatientId}
        userId={user?.id}
        activeDocumentId={documentId ?? ''}
        resolving={!previewDoc.data && Boolean(previewDoc.isLoading)}
        onOpen={openPatientFile}
      />
      {desktop ? (
        <FileSideResizeHandle
          side="files"
          value={sideWidths.files}
          min={FILE_SIDE_WIDTH_MIN}
          max={maxFileSideWidth(viewportWidth, markingOccupied)}
          label={t('documents.browser.resize')}
          onChange={applyFilesWidth}
          onReset={() => applyFilesWidth(FILE_SIDE_WIDTH_DEFAULT)}
        />
      ) : null}
    </aside>
  ) : null;

  return createPortal(
    <div
      className={cn(
        'fp-viewer',
        showPanel && desktop ? !panelOpen && 'is-rail' : 'is-nopanel',
        showFilesTool && desktop && (filesOpen ? 'is-files' : 'is-file-rail'),
        !desktop && sheetOpen && 'is-sheet',
        !desktop && filesSheetOpen && 'is-files-sheet',
      )}
      role="dialog"
      aria-modal="true"
      aria-label={filename}
    >
      <header className="fp-header" data-fp-chrome>
        <span className="fp-header-icon">
          <FileText className="h-5 w-5" aria-hidden />
        </span>
        <div className="fp-header-title">
          <h1>{filename}</h1>
          {subtitle ? <p className="fp-header-sub">{subtitle}</p> : null}
        </div>
        <div className="fp-header-acts">
          <div className="fp-header-desktop flex items-center gap-2">
            {canOpenInNewTab ? (
              <Button type="button" variant="secondary" onClick={handleOpenInNewTab}>
                <ExternalLink />
                {t('documents.open_in_new_tab')}
              </Button>
            ) : null}
            {actionUrl ? (
              downloadMenu
            ) : error ? (
              <Button type="button" variant="secondary" onClick={handleDownload}>
                <Download />
                {t('documents.download')}
              </Button>
            ) : null}
          </div>
          <span className="fp-header-more" data-fp-menu>
            <MenuPopover
              align="end"
              title={t('common.more_actions')}
              trigger={
                <button type="button" className="fp-icon-btn" aria-label={t('common.more_actions')}>
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              }
            >
              {canOpenInNewTab ? (
                <MenuItem icon={<ExternalLink className="h-4 w-4" />} onClick={handleOpenInNewTab}>
                  {t('documents.open_in_new_tab')}
                </MenuItem>
              ) : null}
              {actionUrl || error ? (
                <MenuItem icon={<Download className="h-4 w-4" />} onClick={handleDownload}>
                  {t('documents.highlights.downloadOriginal')}
                </MenuItem>
              ) : null}
              {showPdf ? (
                <MenuItem
                  icon={<Highlighter className="h-4 w-4" />}
                  onClick={() => void handleDownloadMarked()}
                >
                  {t('documents.highlights.downloadWithHighlights')}
                </MenuItem>
              ) : null}
              {persistable ? (
                <MenuItem onClick={toggleHighlights}>
                  {t('documents.highlights.toggleHighlights')}
                </MenuItem>
              ) : null}
            </MenuPopover>
          </span>
          <span className="fp-vr" />
          <Button type="button" variant="secondary" onClick={onClose}>
            <X />
            {t('common.close')}
          </Button>
        </div>
      </header>

      <div
        className="fp-body"
        style={
          {
            ...(desktop && showFilesTool && filesOpen
              ? { '--fp-files': `${sideWidths.files}px` }
              : null),
            ...(desktop && showPanel && panelOpen
              ? { '--fp-mark': `${sideWidths.marking}px` }
              : null),
          } as CSSProperties
        }
      >
        {showFilesTool && desktop && filesOpen ? filesPanel : null}
        {showFilesTool && desktop && !filesOpen ? (
          <CollapsedToolRail
            className="fp-files-rail"
            chrome
            testId="file-browser-rail"
            expanded={false}
            label={t('documents.browser.tab')}
            icon={<FolderOpen className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
            onClick={() => rememberFiles(true)}
          />
        ) : null}
        <section
          ref={docRef}
          className={showPdf || showImage || showFlow ? 'fp-doc has-toolbar' : 'fp-doc'}
          aria-label={filename}
        >
          {showPdf || showImage || showFlow ? (
            <FilePreviewDocToolbar
              mode={showImage ? 'media' : 'document'}
              page={showPdf ? page : 1}
              pageCount={showPdf ? pageCount : 1}
              highlightIndex={
                !showImage && highlightsVisible && selected && highlightIndex
                  ? highlightIndex
                  : null
              }
              highlightTotal={filtered.length}
              zoomPercent={showImage && zoomMode === 'fit' ? 100 : zoomPercent}
              fitActive={zoomMode === 'fit'}
              showHighlightNav={Boolean(
                !showImage &&
                (showPdf || showText || showDocx || showSheet) &&
                highlightsVisible &&
                selected &&
                filtered.length > 0,
              )}
              onPrevHighlight={() => moveHighlight(-1)}
              onNextHighlight={() => moveHighlight(1)}
              onZoomIn={() => applyZoom(liveZoom + 0.25)}
              onZoomOut={() => applyZoom(liveZoom - 0.25)}
              onZoomPreset={(value) => applyZoom(value)}
              onFitWidth={showPdf ? applyFit : showImage ? applyImageFit : applyFlowFit}
              onMenuOpen={setMenuOpen}
            />
          ) : null}
          <div className={showMarginRail ? 'fp-doc-row' : 'fp-doc-row is-norail'}>
            <div
              ref={scrollRef}
              className={showPdf ? 'fp-scroll' : 'fp-scroll is-stage'}
              tabIndex={0}
              aria-label={filename}
              onScroll={onScroll}
              onClick={
                showDocx || showSheet || showText
                  ? (event) => {
                      const target = event.target;
                      if (!(target instanceof Element)) return;
                      if (target.closest('[data-fp-chrome]')) return;
                      const mark = target.closest('mark[data-hl-id], td[data-hl-id]');
                      if (mark instanceof HTMLElement && mark.dataset.hlId) {
                        handleSelectHighlight(mark.dataset.hlId);
                        return;
                      }
                      handleSelectHighlight(null);
                    }
                  : undefined
              }
            >
              {stage ? (
                <FilePreviewStage
                  stage={stage}
                  filename={filename}
                  htmlRef={docxRef}
                  contentZoom={showFlow ? liveZoom : undefined}
                  onContentFrame={showFlow ? measureFlowRings : undefined}
                  onImageError={() => setInlineFailed(true)}
                />
              ) : null}
              {showPdf && previewBlob && (
                <Suspense
                  fallback={
                    <Loader2 className="mx-auto mt-16 h-8 w-8 animate-spin text-ds-muted" />
                  }
                >
                  <PdfCanvasPreview
                    blob={previewBlob}
                    filename={filename}
                    highlights={documentHighlights}
                    selectedHighlightId={selectedId}
                    hoveredHighlightId={hoveredId}
                    highlightsVisible={highlightsVisible || Boolean(selectedId)}
                    pending={draft ? { draft, color: toolbarColor } : null}
                    zoom={liveZoom}
                    committedZoom={committedZoom}
                    scrollRef={scrollRef}
                    onSelectHighlight={handleSelectHighlight}
                    onHoverHighlight={setHoveredId}
                    onTextSelected={handleTextSelected}
                    onPageChange={(next, count) => {
                      setPage(next);
                      setPageCount(count);
                    }}
                    onGeometry={onGeometry}
                    onMetrics={onMetrics}
                    scrollToPage={scrollToPage}
                  />
                </Suspense>
              )}
            </div>
            {showMarginRail ? (
              <FileHighlightRail
                highlights={highlightsVisible ? filtered : []}
                anchors={showPdf ? undefined : flowAnchors}
                selectedId={selectedId}
                hoveredId={hoveredId}
                highlightsVisible={highlightsVisible}
                pages={pageMetrics}
                scrollHeight={scrollHeight}
                innerRef={railInnerRef}
                onSelect={handleSelectHighlight}
                onHover={setHoveredId}
              />
            ) : null}
          </div>
        </section>
        {showPanel && desktop && panelOpen ? panel : null}
        {showPanel && desktop && !panelOpen ? (
          <CollapsedToolRail
            className="fp-mark-rail"
            chrome
            testId="file-marking-rail"
            expanded={false}
            label={t('documents.highlights.panel_title')}
            icon={<Highlighter className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
            onClick={() => rememberPanel(true)}
          />
        ) : null}
        {!desktop ? panel : null}
        {!desktop ? filesPanel : null}
      </div>

      {(showPanel || showFilesTool) && !desktop ? (
        <div className="fp-sheet-bar">
          {showPanel ? (
            <Button
              type="button"
              variant="secondary"
              className="fp-sheet-btn"
              onClick={() => {
                setSheetOpen((open) => !open);
                setFilesSheetOpen(false);
              }}
            >
              {t('documents.highlights.openSheet', { count: numbered.length })}
            </Button>
          ) : null}
          {showFilesTool ? (
            <Button
              type="button"
              variant="secondary"
              className="fp-sheet-btn"
              data-testid="file-browser-sheet"
              onClick={() => {
                setFilesSheetOpen((open) => !open);
                setSheetOpen(false);
              }}
            >
              {t('documents.browser.tab')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {deleteId ? (
        <FilePreviewConfirm
          title={t('documents.highlights.deleteConfirmTitle')}
          description={t('documents.highlights.deleteConfirmBody')}
          confirmLabel={t('documents.highlights.delete')}
          danger
          pending={deleteHighlight.isPending}
          onConfirm={() => void handleConfirmDelete()}
          onClose={() => setDeleteId(null)}
        />
      ) : null}
      {discardKind ? (
        <FilePreviewConfirm
          title={
            discardKind === 'draft'
              ? t('documents.highlights.discardComment')
              : t('documents.highlights.discardEdit')
          }
          confirmLabel={t('common.yes')}
          onConfirm={() => {
            if (discardKind === 'draft') clearDraft();
            else {
              setEditingId(null);
              setEditError(null);
            }
            setDiscardKind(null);
          }}
          onClose={() => setDiscardKind(null)}
        />
      ) : null}
    </div>,
    document.body,
  );
}
